import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { MaskTextLoad, MaskText, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { JOURNEY } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);

  return (
    <section ref={ref} className="relative min-h-[92svh] flex items-end overflow-hidden" data-testid="journey-hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={JOURNEY.heroImage} alt="Choices & Decisions across Africa" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#1B0812] via-[#1B0812]/55 to-[#1B0812]/25" />
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 md:px-10 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 1 }}
          className="font-display text-xs md:text-sm uppercase tracking-[0.3em] text-coral mb-6"
        >
          {JOURNEY.kicker}
        </motion.p>
        <h1 className="font-serif font-light text-[11vw] md:text-[5.2vw] leading-[0.92] text-cream">
          <MaskTextLoad lines={JOURNEY.title} delay={0.3} stagger={0.12} />
        </h1>
      </div>
    </section>
  );
};

const Intro = () => (
  <section className="relative bg-[#1B0812] py-24 md:py-36 px-5 md:px-10 grain" data-testid="journey-intro">
    <div className="max-w-3xl mx-auto space-y-8">
      {JOURNEY.intro.map((p, i) => (
        <FadeUp key={i} delay={0.03 * i}>
          <p className={`font-serif font-light ${i === 0 ? "text-3xl md:text-5xl leading-[1.1] text-cream" : "text-xl md:text-2xl leading-relaxed text-cream/70"}`}>
            {p}
          </p>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Chapter = ({ chapter, index }) => {
  const flipped = index % 2 === 1;
  return (
    <section className="relative bg-[#1B0812] py-16 md:py-24 px-5 md:px-10" data-testid={`journey-chapter-${chapter.id}`}>
      <div className={`max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flipped ? "" : ""}`}>
        {/* Image */}
        <FadeUp className={flipped ? "lg:order-2" : ""}>
          <div className="relative overflow-hidden rounded-[2rem] card-shadow aspect-[4/5] sm:aspect-[16/12] bg-[#12060D]">
            <img src={chapter.image} alt={chapter.city} className={`h-full w-full ${chapter.fit === "contain" ? "object-contain p-2" : "object-cover"}`} />
            <div className="absolute inset-0 ring-1 ring-inset ring-cream/10 rounded-[2rem]" />
          </div>
        </FadeUp>

        {/* Copy */}
        <div className={flipped ? "lg:order-1" : ""}>
          <FadeUp>
            <div className="inline-flex items-center gap-2 font-display text-[11px] uppercase tracking-[0.2em] text-coral mb-4">
              <MapPin size={13} /> {chapter.country}
            </div>
          </FadeUp>
          <h2 className="font-serif font-light text-6xl md:text-7xl leading-[0.9] text-cream">
            <MaskText lines={[chapter.city]} />
          </h2>
          <FadeUp delay={0.1}>
            <p className="mt-3 font-serif italic text-xl md:text-2xl text-gradient">{chapter.tagline}</p>
          </FadeUp>

          <div className="mt-8 space-y-5">
            {chapter.before.map((p, i) => (
              <FadeUp key={i} delay={0.03 * i}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-cream/70">{p}</p>
              </FadeUp>
            ))}
          </div>

          {chapter.pairs && (
            <Stagger className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {chapter.pairs.map(([a, b]) => (
                <StaggerItem key={a}>
                  <div className="rounded-2xl border border-cream/10 bg-cream/[0.04] px-6 py-5">
                    <span className="font-serif text-3xl md:text-4xl text-cream">{a}</span>
                    <span className="block font-sans text-sm text-cream/55 mt-1">{b}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          )}

          {chapter.quote && (
            <FadeUp delay={0.1}>
              <blockquote className="mt-8 border-l-2 border-coral pl-6 font-serif italic font-light text-2xl md:text-3xl leading-snug text-cream">
                “{chapter.quote}”
              </blockquote>
            </FadeUp>
          )}

          {chapter.after && (
            <div className="mt-8 space-y-5">
              {chapter.after.map((p, i) => (
                <FadeUp key={i} delay={0.03 * i}>
                  <p className="font-sans text-base md:text-lg leading-relaxed text-cream/70">{p}</p>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Legacy = () => {
  const { legacy } = JOURNEY;
  return (
    <section className="relative overflow-hidden py-28 md:py-40 px-5 md:px-10" data-testid="journey-legacy">
      <div className="absolute inset-0 brand-gradient" />
      <div className="absolute inset-0 grain" />
      <div className="relative z-10 max-w-3xl mx-auto">
        <FadeUp>
          <p className="font-display text-xs uppercase tracking-[0.25em] text-white/80 mb-8">{legacy.kicker}</p>
        </FadeUp>
        {legacy.intro.map((p, i) => (
          <FadeUp key={i}>
            <p className="font-serif font-light text-2xl md:text-3xl leading-snug text-white">{p}</p>
          </FadeUp>
        ))}

        <div className="mt-10 space-y-2">
          {legacy.choosing.map((c, i) => (
            <FadeUp key={c} delay={0.04 * i}>
              <p className="font-sans text-lg md:text-xl text-white/90">{c}</p>
            </FadeUp>
          ))}
        </div>

        <div className="mt-12 space-y-6">
          {legacy.body.map((p, i) => (
            <FadeUp key={i} delay={0.02 * i}>
              <p className="font-sans text-base md:text-lg leading-relaxed text-white/85">{p}</p>
            </FadeUp>
          ))}
        </div>

        <div className="mt-14 space-y-1">
          {legacy.crescendo.map((c, i) => (
            <h3 key={c} className="font-serif font-light text-3xl md:text-5xl leading-[1.05] text-white">
              <MaskText lines={[c]} delay={i * 0.05} />
            </h3>
          ))}
        </div>

        <FadeUp delay={0.15}>
          <div className="mt-14 flex flex-wrap gap-4">
            <Link
              to="/gallery?edition=Sierra Leone"
              data-testid="journey-gallery-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-display text-xs uppercase tracking-[0.14em] font-bold text-magenta transition-all duration-300 hover:scale-105"
            >
              See Sierra Leone
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/register"
              data-testid="journey-register-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 font-display text-xs uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:bg-white/10"
            >
              Bring it to Your City
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Journey() {
  return (
    <div className="bg-[#1B0812]">
      <Hero />
      <Intro />
      {JOURNEY.chapters.map((c, i) => (
        <Chapter key={c.id} chapter={c} index={i} />
      ))}
      <Legacy />
    </div>
  );
}
